import React from "react";
import { Box, Paper, IconButton, Typography, Popover } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryIcon from "@mui/icons-material/Category";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import EventIcon from "@mui/icons-material/Event";
import StraightenIcon from "@mui/icons-material/Straighten";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NaturePeopleIcon from "@mui/icons-material/NaturePeople";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { Treatment } from "../types/treatment";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { formatArea, formatFuelLoad, formatLength } from "../utils/units";
import { useTimezone } from "../contexts/TimezoneContext";
import { formatInTimezone } from "../utils/datetime";

interface TreatmentPopoverProps {
  treatment: Treatment | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onEdit: (treatment: Treatment) => void;
  onDelete: (treatment: Treatment) => void;
}

// Helper function to check if a date string is valid
const isValidDate = (dateString: string | number | Date): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

const TreatmentPopover: React.FC<TreatmentPopoverProps> = ({
  treatment,
  anchorPosition,
  onClose,
  onEdit,
  onDelete,
}) => {
  const unitSystem = useUnitSystem();
  const { selectedTimezone } = useTimezone();
  const open = Boolean(treatment && anchorPosition);
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);

  if (!treatment) return null;

  const treatmentType = (treatment.properties.Type || "")
    .toString()
    .toLowerCase();
  const rawResidualFuel = treatment.properties.ResidualFuel;
  const residualFuelValue =
    rawResidualFuel === undefined || rawResidualFuel === null
      ? null
      : Number(rawResidualFuel);
  const hasResidualFuel =
    rawResidualFuel !== undefined &&
    rawResidualFuel !== null &&
    !Number.isNaN(residualFuelValue) &&
    residualFuelValue !== 0 &&
    !treatmentType.includes("reveg");
  const vegDate = treatment.properties.VegDate;
  const vegClass = treatment.properties.VegClass;
  const totalCostRaw = treatment.properties.Cost;
  const totalCost =
    totalCostRaw === undefined || totalCostRaw === null
      ? null
      : Number(totalCostRaw);

  const widthMetersRaw = treatment.properties.width;
  const widthMeters =
    widthMetersRaw === undefined || widthMetersRaw === null
      ? null
      : Number(widthMetersRaw);

  const lengthMetersRaw = treatment.properties.Length;
  const lengthMeters =
    lengthMetersRaw === undefined || lengthMetersRaw === null
      ? null
      : Number(lengthMetersRaw);

  const sizeHaRaw = treatment.properties.SizeHa;
  const sizeHa =
    sizeHaRaw === undefined || sizeHaRaw === null ? null : Number(sizeHaRaw);

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Paper sx={{ p: 2, maxWidth: 300, bgcolor: "#212b37", color: "white" }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
          {treatment.properties.Name || "Unnamed Treatment"}
        </Typography>

        {treatment.properties.Type && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <CategoryIcon sx={{ mr: 1, fontSize: 18 }} />
            Type: {treatment.properties.Type}
          </Typography>
        )}

        {hasResidualFuel && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <LocalFireDepartmentIcon sx={{ mr: 1, fontSize: 18 }} />
            Residual Fuel: {formatFuelLoad(residualFuelValue as number, unitSystem)}
          </Typography>
        )}

        {treatment.properties.StartDate &&
          isValidDate(treatment.properties.StartDate) && (
            <Typography
              variant="body2"
              sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
            >
              <EventIcon sx={{ mr: 1, fontSize: 18 }} />
              Start Date:{" "}
              {formatInTimezone(
                String(treatment.properties.StartDate),
                selectedTimezone,
                "DD/MM/YYYY"
              )}
            </Typography>
          )}

        {treatment.properties.EndDate &&
          isValidDate(treatment.properties.EndDate) && (
            <Typography
              variant="body2"
              sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
            >
              <EventIcon sx={{ mr: 1, fontSize: 18 }} />
              End Date:{" "}
              {formatInTimezone(
                String(treatment.properties.EndDate),
                selectedTimezone,
                "DD/MM/YYYY"
              )}
            </Typography>
          )}

        {treatmentType.includes("reveg") && vegDate && isValidDate(vegDate) && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
            Vegetation Date:{" "}
            {formatInTimezone(String(vegDate), selectedTimezone, "DD/MM/YYYY")}
          </Typography>
        )}

        {treatmentType.includes("reveg") && vegClass && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <NaturePeopleIcon sx={{ mr: 1, fontSize: 18 }} />
            Vegetation Class: {vegClass}
          </Typography>
        )}

        {totalCost !== null && !Number.isNaN(totalCost) && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <AttachMoneyIcon sx={{ mr: 1, fontSize: 18 }} />
            Total cost: ${totalCost.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </Typography>
        )}

        {widthMeters !== null && !Number.isNaN(widthMeters) && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <StraightenIcon sx={{ mr: 1, fontSize: 18 }} />
            Width: {formatLength(widthMeters, unitSystem)}
          </Typography>
        )}

        {lengthMeters !== null && !Number.isNaN(lengthMeters) && (
          <Typography
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
          >
            <StraightenIcon sx={{ mr: 1, fontSize: 18 }} />
            Length: {formatLength(lengthMeters, unitSystem)}
          </Typography>
        )}

        {sizeHa !== null && !Number.isNaN(sizeHa) && (
          <Typography
            variant="body2"
            sx={{ mb: 1, display: "flex", alignItems: "center" }}
          >
            <StraightenIcon sx={{ mr: 1, fontSize: 18 }} />
            Size: {formatArea(sizeHa, unitSystem)}
          </Typography>
        )}

        {!viewOnly && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <IconButton
              size="small"
              onClick={() => onEdit(treatment)}
              sx={{ color: "#00d4ad", mr: 1 }}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              size="small"
              onClick={() => onDelete(treatment)}
              sx={{ color: "#ff5252" }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Popover>
  );
};

export default TreatmentPopover;
